import { useCallback, useEffect, useState } from 'react';
import { type ApiError, fetchBalanceDashboard, parseApiError } from './api';
import type { BalanceDashboard } from './types';

/**
 * Loads the balance on entry. Fetch-once — no background polling (PRD §9 / O-6:
 * the source page sets no refresh interval).
 *
 * Error model (PRD §9): a failed initial load (any non-2xx) enters the `error`
 * phase, exposing the parsed `{ status, errorCode }` so the screen can tell a
 * non-retryable 403 (FORBIDDEN) apart from retryable failures. `reload`
 * re-issues the GET.
 */
export type BalancePhase = 'loading' | 'ready' | 'error';

export const useBalanceDashboard = (): {
  phase: BalancePhase;
  data: BalanceDashboard | null;
  error: ApiError | null;
  reload: () => void;
} => {
  const [phase, setPhase] = useState<BalancePhase>('loading');
  const [data, setData] = useState<BalanceDashboard | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    setPhase('loading');
    setError(null);
    try {
      const res = await fetchBalanceDashboard();
      setData(res);
      setPhase('ready');
    } catch (e) {
      setData(null);
      setError(parseApiError(e));
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { phase, data, error, reload: () => void load() };
};
